import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GetData } from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import './Products.css';

const ITEMS_PER_PAGE = 12;

const Products = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const search = searchParams.get('search') || '';
    const categoryId = searchParams.get('categoryId') || '';
    const featured = searchParams.get('featured') || '';

    setSearchQuery(search);

    if (search) {
      setSelectedCategory('');
    } else if (categoryId) {
      setSelectedCategory(categoryId);
    } else if (featured) {
      setSelectedCategory('');
    }
  }, [searchParams]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams();

        if (searchQuery) {
          params.append('search', searchQuery);
        }

        if (selectedCategory && !searchQuery) {
          params.append('categoryId', selectedCategory);
        }

        const featured = searchParams.get('featured');
        if (featured && !searchQuery && !selectedCategory) {
          params.append('featured', featured);
        }

        const endpoint = `/products${params.toString() ? `?${params.toString()}` : ''}`;
        const data = await GetData(endpoint);
        setAllProducts(data.products || []);
        setVisibleCount(ITEMS_PER_PAGE);
      } catch (error) {
        console.error('Error cargando productos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [selectedCategory, searchQuery, searchParams]);

  // IntersectionObserver para infinite scroll
  useEffect(() => {
    if (loading || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          setVisibleCount(prev => {
            if (prev >= allProducts.length) return prev;
            loadingMoreRef.current = true;
            setLoadingMore(true);

            setTimeout(() => {
              setVisibleCount(p => p + ITEMS_PER_PAGE);
              setLoadingMore(false);
              loadingMoreRef.current = false;
            }, 1000);

            return prev;
          });
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loading, allProducts.length, visibleCount]);

  const loadData = async () => {
    try {
      const data = await GetData('/categories');
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  };

  const handleCategoryChange = (categoryId) => {
    navigate('/products');
    setSelectedCategory(categoryId);
    setSearchQuery('');
  };

  const visibleProducts = allProducts.slice(0, visibleCount);
  const hasMore = visibleCount < allProducts.length;

  return (
    <div className="products-page">
      <div className="container">
        <h1 className="page-title">
          {searchQuery ? `Resultados de busqueda: "${searchQuery}"` :
           searchParams.get('featured') ? 'Ofertas Especiales' :
           selectedCategory ? categories.find(c => c.id === selectedCategory)?.name || 'Productos' :
           'Todos los Productos'}
        </h1>

        {/* Filtros */}
        <div className="filters">
          <button
            className={`filter-btn ${selectedCategory === '' && !searchQuery ? 'active' : ''}`}
            onClick={() => handleCategoryChange('')}
          >
            Todos
          </button>
          {categories.map(category => (
            <button
              key={category.id}
              className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Productos */}
        {loading ? (
          <div className="loading">Cargando productos...</div>
        ) : allProducts.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron productos</p>
          </div>
        ) : (
          <>
            <div className="grid grid-4">
              {visibleProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {hasMore && (
              <>
                <div ref={sentinelRef} style={{ height: 1 }} />
                {loadingMore && (
                  <div className="products-loading-more">
                    <div className="products-spinner" />
                    {/* <span>Cargando mas productos...</span> */}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Products;
